import { useState, useEffect } from 'react';
import { useLegalStore } from '../store/legal.store';
import { toast } from 'react-hot-toast';
import { logger } from '@/features/help/utils/logger';

/**
 * Component for editing the disclaimer text in the Settings page
 */
export const DisclaimerSettings = () => {
  const { companyName, disclaimerText, isDefault, isLoading, fetchDisclaimer, updateDisclaimer } =
    useLegalStore();
  const [editedCompanyName, setEditedCompanyName] = useState('');
  const [editedDisclaimerText, setEditedDisclaimerText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Load disclaimer on component mount
  useEffect(() => {
    const loadDisclaimer = async () => {
      await fetchDisclaimer();
    };
    loadDisclaimer();
  }, []);

  // Update form values when store values change
  useEffect(() => {
    if (companyName) {
      setEditedCompanyName(companyName);
    }
    if (disclaimerText) {
      setEditedDisclaimerText(disclaimerText);
    }
  }, [companyName, disclaimerText]);

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editedCompanyName.trim()) {
      toast.error('Company name is required');
      return;
    }

    if (!editedDisclaimerText.trim()) {
      toast.error('Disclaimer text is required');
      return;
    }

    setIsSaving(true);

    try {
      logger.info('Legal', 'Updating disclaimer settings');
      await updateDisclaimer(editedCompanyName, editedDisclaimerText);
      toast.success('Disclaimer settings saved successfully');
      setIsEditing(false);
    } catch (error) {
      logger.error('Legal', 'Failed to update disclaimer settings', { error });
      toast.error('Failed to save disclaimer settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Reset form to current values
   */
  const handleCancel = () => {
    setEditedCompanyName(companyName || '');
    setEditedDisclaimerText(disclaimerText || '');
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
        <div className="h-10 bg-gray-200 rounded w-1/4"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Legal Disclaimer</h2>

      {isEditing ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
              Company Name
            </label>
            <input
              id="companyName"
              type="text"
              value={editedCompanyName}
              onChange={(e) => setEditedCompanyName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Your company name"
              required
            />
          </div>

          <div>
            <label
              htmlFor="disclaimerText"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Legal Disclaimer Text
            </label>
            <textarea
              id="disclaimerText"
              value={editedDisclaimerText}
              onChange={(e) => setEditedDisclaimerText(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your legal disclaimer text here"
              required
            />
            <p className="mt-1 text-sm text-gray-500">
              This text will be displayed on all application forms for signature.
            </p>
          </div>

          <div className="flex space-x-3 pt-2">
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Company Name</h3>
            <p className="mt-1 text-gray-900">{companyName}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700">Legal Disclaimer</h3>
            <div className="mt-1 p-3 bg-gray-50 rounded border border-gray-200 text-sm text-gray-800 whitespace-pre-wrap">
              {disclaimerText}
            </div>
            {isDefault && (
              <p className="mt-1 text-xs text-amber-600 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                This is a default disclaimer. You can edit it to match your company's specific
                requirements.
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Edit Disclaimer
          </button>
        </div>
      )}
    </div>
  );
};
